import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ModalComponent } from 'src/app/common/modules/modal.module/modal/modal.component';
import { Medic } from 'src/app/entities/medic.entity/medic';
import { MedicPrescription } from 'src/app/entities/prescription.entity/medic-prescription.entity/medic-prescription';
import { MedicsService } from 'src/app/services/medics.service/medics.service';
import { MedicFormComponent } from '../../medic-form.module/medic-form.component/medic-form.component';
import { MedicFilter } from '../../medic-form.module/medic-select-drawer.component/medic-select-drawer.component';

export class PrescriptionItemValidation {
  controlsValidation: Record<symbol, symbol | null> = {}
  get isValid(): boolean {
    return Array.from(Object.values(this.controlsValidation)).every(elm => elm === null)
  }
}

export class MedicPrescriptionItemValidation extends PrescriptionItemValidation {
  controlsValidation: { medic: 'required' | null, posology: 'required' | null, duration: 'required' | 'number' | 'min' | null, notes: null } = {
    medic: null,
    posology: null,
    duration: null,
    notes: null
  };

  constructor(controlsValidation: { medic: 'required' | null, posology: 'required' | null, duration: 'required' | 'number' | 'min' | null, notes: null }) {
    super()
    this.controlsValidation = controlsValidation
  }

  static validate(medicPrescriptionItem: MedicPrescription): MedicPrescriptionItemValidation {
    return (medicPrescriptionItem.medic != undefined && medicPrescriptionItem.medic?.idMedicament != undefined) ? new MedicPrescriptionItemValidation({
      medic: null,
      duration: medicPrescriptionItem.duration ? (!isNaN(Number(medicPrescriptionItem.duration)) ? (Number(medicPrescriptionItem.duration) < 1 ? 'min' : null) : 'number') : 'required',
      posology: !medicPrescriptionItem.posology?.length ? 'required' : null,
      notes: null
    }) : new MedicPrescriptionItemValidation({ medic: null, posology: null, duration: null, notes: null });
  }
}

@Component({
  selector: 'app-medic-prescription-form',
  templateUrl: './medic-prescription-form.component.html',
  styleUrls: ['./medic-prescription-form.component.css']
})
export class MedicPrescriptionFormComponent implements OnInit {

  @Input() medics: Array<Medic> = new Array();
  private _medicPrescriptionItems: Array<MedicPrescription> = new Array();
  @Output() medicPrescriptionItemsChange: EventEmitter<Array<MedicPrescription>> = new EventEmitter();
  _isSubmited: boolean = false;
  drawerActive: boolean = false;
  medicsFilter: Array<MedicFilter> = new Array();

  @Input() set medicPrescriptionItems(value: Array<MedicPrescription>) {
    this._medicPrescriptionItems = value;
    // Appliquer le tri croissant à chaque fois que la liste est modifiée
    this.sortAscendingByColumnsNbre();
  }

  get medicPrescriptionItems(): Array<MedicPrescription> {
    return this._medicPrescriptionItems;
  }

  @Input() set submitted(v: boolean) {
    if (v) this.medicPrescriptionItems = this.medicPrescriptionItems.filter(e => {
      return (e.medic != undefined && e.medic.idMedicament != undefined)
    })
    this._isSubmited = v;
  }
  @Output() validation: EventEmitter<boolean> = new EventEmitter()
  @ViewChild(ModalComponent, { read: ModalComponent, static: false })
  dialog!: ModalComponent;
  get submitted(): boolean {
    return this._isSubmited;
  }
  @ViewChild('medicForm', { read: MedicFormComponent, static: true }) medicForm!: MedicFormComponent;
  get isValid(): boolean {
    return this.medicPrescriptionItems.every(item => {
      return MedicPrescriptionItemValidation.validate(item).isValid
    })
  }

  constructor(private medicsService: MedicsService, private toasterService: ToastrService) { }

  validatePrescriptionItem(item: MedicPrescription) {
    return MedicPrescriptionItemValidation.validate(item)
  }

  ngOnInit(): void {
    // Charger les médicaments
    this.medicsService.all().subscribe(medics => {
      this.medics = Array.from(medics, m => Medic.fromResponse(m))
    }, error => { this.toasterService.error('Enable to load medics') });
    
    // Initialiser les index si nécessaire
    this.updateIndexes();
    
    // Appliquer le tri CROISSANT dans ngOnInit
    this.sortAscendingByColumnsNbre();
  }

  /**
   * Trie les éléments par columnsNbre en ordre CROISSANT (0,1,2,3,...)
   * Cette méthode modifie directement le tableau original
   */
  private sortAscendingByColumnsNbre(): void {
    if (this._medicPrescriptionItems && this._medicPrescriptionItems.length > 0) {
      this._medicPrescriptionItems.sort((a, b) => {
        const aValue = a.columnsNbre ?? 0;
        const bValue = b.columnsNbre ?? 0;
        return aValue - bValue; // Ordre CROISSANT
      });
    }
  }

  /**
   * Met à jour la propriété columnsNbre pour chaque élément
   * basé sur sa position dans le tableau
   */
  private updateIndexes(): void {
    this._medicPrescriptionItems.forEach((item, index) => {
      item.columnsNbre = index;
    });
  }

  /**
   * Nettoie et filtre les éléments par columnsNbre
   * Supprime les doublons et réindexe
   */
  private filterAndCleanByIndex(): void {
    // Utiliser un Map pour garder les éléments uniques par columnsNbre
    const uniqueItems = new Map<number, MedicPrescription>();
    
    this._medicPrescriptionItems.forEach(item => {
      if (item.columnsNbre !== undefined && !uniqueItems.has(Number(item.columnsNbre))) {
        uniqueItems.set(Number(item.columnsNbre), item);
      } else if (item.columnsNbre === undefined) {
        // Si pas d'index, on en assigne un nouveau temporaire
        const newIndex = uniqueItems.size;
        item.columnsNbre = newIndex;
        uniqueItems.set(newIndex, item);
      }
    });
    
    // Convertir le Map en tableau
    this._medicPrescriptionItems = Array.from(uniqueItems.values());
    
    // Re-synchroniser les index
    this.updateIndexes();
    // Réappliquer le tri
    this.sortAscendingByColumnsNbre();
  }

  /**
   * Récupère les médicaments depuis le drawer et les ajoute
   */
  closed(): void {
    // Filtrer les éléments sélectionnés
    const selectedMedics = this.medicsFilter.filter(elm => elm.selected && elm.medicPrescription);
    
    // Ajouter les nouveaux médicaments
    selectedMedics.forEach((elm) => {
      // Créer une nouvelle instance si nécessaire
      const newPrescription = elm.medicPrescription;
      
      // Vérifier si le médicament n'existe pas déjà dans la liste
      const exists = this._medicPrescriptionItems.some(item => 
        item.medic?.idMedicament === newPrescription.medic?.idMedicament
      );
      
      if (!exists) {
        // Assigner un nouvel index
        newPrescription.columnsNbre = this._medicPrescriptionItems.length;
        this._medicPrescriptionItems.push(newPrescription);
      }
    });
    
    // Synchroniser tous les index
    this.updateIndexes();
    
    // Vérifier et supprimer les doublons de médicaments
    this.removeDuplicateMedics();
    
    // Réappliquer le tri après ajout
    this.sortAscendingByColumnsNbre();
    
    // Fermer le drawer
    this.drawerActive = false;
    
    // Notifier les changements
    this.change();
  }

  /**
   * Supprime les doublons de médicaments dans la liste
   */
  private removeDuplicateMedics(): void {
    const uniqueMedics = new Map<number, MedicPrescription>();
    
    this._medicPrescriptionItems.forEach(item => {
      if (item.medic?.idMedicament) {
        const medicId = item.medic.idMedicament;
        if (!uniqueMedics.has(medicId)) {
          uniqueMedics.set(medicId, item);
        }
      } else {
        // Garder les éléments sans médicament (nouveaux)
        uniqueMedics.set(uniqueMedics.size, item);
      }
    });
    
    // Remplacer par la liste unique
    this._medicPrescriptionItems = Array.from(uniqueMedics.values());
    
    // Réindexer
    this.updateIndexes();
    // Réappliquer le tri
    this.sortAscendingByColumnsNbre();
  }

  deepEqual(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  /**
   * Ajoute une nouvelle ligne de prescription vide
   */
  addPrescriptionItem(): void {
    let medicPrescription: MedicPrescription = new MedicPrescription({ medic: null as any });
    medicPrescription.medic = null as any;
    medicPrescription.posology = '';
    medicPrescription.duration = 1;
    medicPrescription.unit = 0;
    medicPrescription.note = '';
    
    // Assigner l'index avant d'ajouter
    medicPrescription.columnsNbre = this._medicPrescriptionItems.length;
    
    this._medicPrescriptionItems.push(medicPrescription);
    this.updateIndexes();
    this.sortAscendingByColumnsNbre(); // Appliquer le tri après ajout
    this.change();
  }

  /**
   * Supprime un élément par son index
   */
  deletePrescriptionItem(index: number): void {
    if (index >= 0 && index < this._medicPrescriptionItems.length) {
      this._medicPrescriptionItems.splice(index, 1);
      // Réindexer après suppression
      this.updateIndexes();
      this.sortAscendingByColumnsNbre(); // Appliquer le tri après suppression
      this.change();
    }
  }

  /**
   * Modifie un élément
   */
  onEditPrescriptionItem(index: number): void {
    // Mettre à jour l'index si nécessaire
    if (this._medicPrescriptionItems[index]) {
      this._medicPrescriptionItems[index].columnsNbre = index;
    }
    this.sortAscendingByColumnsNbre(); // Appliquer le tri après modification
    this.change();
  }

  addMedicSubmit(): void {
    this.medicForm.isSubmitted = true;
    if (this.medicForm.isValid) {
      this.medicsService.add(this.medicForm.medic).subscribe(newMedic => {
        this.medics = [newMedic, ...this.medics];
        this.medicForm.isSubmitted = false;
        this.medicForm.medic = new Medic();
      }, error => { this.toasterService.error('Enable to add medic') });
    }
  }

  onAddMedic(): void {
    this.medicForm.isSubmitted = true;
    if (this.medicForm.isValid) {
      this.medicsService.add(this.medicForm.medic).subscribe(medic => {
        this.medicForm.isSubmitted = false;
        this.medics = [medic, ...this.medics];
        this.medicForm.medic = Medic.fromResponse(medic);
        this._medicPrescriptionItems.forEach(elm => { elm.medic = medic });
        this.sortAscendingByColumnsNbre(); // Appliquer le tri après ajout de médicament
        this.change();
        this.medicPrescriptionItemsChange.emit(this._medicPrescriptionItems);
      }, error => this.toasterService.error('Enable to add medic'));
    }
  }

  /**
   * Émet les changements vers le parent (PUBLIC - accessible depuis le template)
   */
  public change(): void {
    // Toujours trier avant d'émettre
    this.sortAscendingByColumnsNbre();
    this.medicPrescriptionItemsChange.emit(this._medicPrescriptionItems);
    this.validation.emit(this.isValid);
  }
  
  /**
   * Ouvre le drawer
   */
  openDrawer(): void {
    this.drawerActive = true;
  }

  /**
   * Force le tri CROISSANT par columnsNbre (méthode publique)
   */
  public sortAscByColumnsNbre(): void {
    this.sortAscendingByColumnsNbre();
    this.change();
  }

  /**
   * Affiche seulement les éléments avec columnsNbre 0, 1, 2
   */
  filterColumnsNbreZeroToOneTwo(): void {
    this._medicPrescriptionItems = this._medicPrescriptionItems.filter(item => {
      const value = item.columnsNbre ?? 0;
      return value === 0 || value === 1 || value === 2;
    });
    this.updateIndexes();
    this.sortAscendingByColumnsNbre(); // Réappliquer le tri après filtrage
    this.change();
  }

  /**
   * Garde seulement les éléments avec columnsNbre <= 2
   */
  keepOnlyColumnsNbreLessThanOrEqualTwo(): void {
    this._medicPrescriptionItems = this._medicPrescriptionItems.filter(item => {
      const value = item.columnsNbre ?? 0;
      return value <= 2;
    });
    this.updateIndexes();
    this.sortAscendingByColumnsNbre(); // Réappliquer le tri après filtrage
    this.change();
    this.toasterService.info(`Gardé seulement les éléments avec columnsNbre <= 2`);
  }

  /**
   * Réinitialise le tri et réindexe
   */
  resetAndReindex(): void {
    this.updateIndexes();
    this.sortAscendingByColumnsNbre();
    this.change();
    this.toasterService.info('Réinitialisation et tri effectués');
  }

  /**
   * Retourne la liste triée (getter)
   */
  getSortedList(): Array<MedicPrescription> {
    return [...this._medicPrescriptionItems];
  }

  /**
   * Compte le nombre d'éléments avec une valeur columnsNbre spécifique
   */
  getCountByColumnsNbre(value: number): number {
    return this._medicPrescriptionItems.filter(item => (item.columnsNbre ?? 0) === value).length;
  }
}